import React, { useState, useEffect, useRef, useMemo } from 'react'
import { AutoComplete, Button, Popover, Spin } from 'antd'
import { Building } from 'lucide-react'
import type { LineItem } from '../../types/ffe'
import { useFFEStore } from '../../store/ffeStore'
import { SupplierCard } from './SupplierCard'
import { useApi } from '../../services/ApiContext'

interface Props {
  item: LineItem
  onUpdate: (updates: Partial<LineItem>) => void
}

export const LineItemSupplier: React.FC<Props> = ({ item, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const inputRef = useRef<any>(null)
  const suppliers = useFFEStore((state) => state.suppliers)
  const addSupplier = useFFEStore((state) => state.addSupplier)
  const [tempSupplier, setTempSupplier] = useState('')
  const [loading, setLoading] = useState(false)
  const api = useApi()

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isEditing])

  const handleSubmit = () => {
    // console.log('handleSubmit', tempSupplier)
    // if (tempSupplier) {
    //   if (suppliers.some((s) => s.name === tempSupplier)) {
    //     const supplier = suppliers.find((s) => s.name === tempSupplier)
    //     if (supplier) {
    //       onUpdate({ supplier: supplier.id })
    //     }
    //   } else {
    //     const newSupplier = { id: crypto.randomUUID(), name: tempSupplier }
    //     addSupplier(newSupplier)
    //     onUpdate({ supplier: newSupplier.id })
    //   }
    //   setTempSupplier('')
    // }
    setIsEditing(false)
  }

  const handleSelect = async (value: string) => {
    const activeSuppliers = suppliers.filter((s) => !s.isArchived)

    value = value.replace('|', '')
    if (value) {
      if (
        activeSuppliers.some(
          (s) => s.name.toLowerCase() === value.toLowerCase()
        )
      ) {
        const supplier = activeSuppliers.find(
          (s) => s.name.toLowerCase() === value.toLowerCase()
        )
        if (supplier) {
          onUpdate({ supplier: supplier._id })
        }
      } else {
        const newSupplier = { name: value }
        setLoading(true)
        const response = await api.createSupplier(newSupplier)
        setLoading(false)
        addSupplier(response)
        onUpdate({ supplier: response._id })
      }
      setTempSupplier('')
    }
    setIsEditing(false)
  }

  const selectedSupplier = useMemo(
    () => suppliers.find((supplier) => supplier._id === item.supplier),
    [item.supplier, suppliers]
  )

  const getSupplierOptions = () => {
    const activeSuppliers = suppliers.filter((s) => !s.isArchived)
    if (
      tempSupplier &&
      !activeSuppliers.some(
        (s) => s.name.toLowerCase() === tempSupplier.toLowerCase()
      )
    ) {
      return [
        ...activeSuppliers
          .map((s) => ({ value: s.name }))
          .filter((s) =>
            s.value.toLowerCase().includes(tempSupplier.toLowerCase())
          )
          .slice(0, 10),
        { value: '|' + tempSupplier, label: `+ Add Supplier '${tempSupplier}'` }
      ]
    }
    return activeSuppliers.map((s) => ({ value: s.name })).filter((s) =>
      s.value.toLowerCase().includes(tempSupplier.toLowerCase())
    ).slice(0, 10)
  }

  if (loading) {
    return (
      <Spin spinning>
        <AutoComplete
          value={tempSupplier}
          onChange={setTempSupplier}
          onBlur={handleSubmit}
          open={true}
          options={getSupplierOptions()}
          placeholder="Enter supplier name"
          className="w-[200px]"
        />
      </Spin>
    )
  }

  if (isEditing) {
    return (
      <AutoComplete
        defaultActiveFirstOption
        ref={inputRef}
        value={tempSupplier}
        onChange={setTempSupplier}
        onBlur={handleSubmit}
        onSelect={handleSelect}
        open={true}
        options={getSupplierOptions()}
        placeholder="Enter supplier name"
        className="w-[200px]"
      />
    )
  }

  if (!selectedSupplier) {
    return (
      <Button
        type="text"
        icon={<Building className="h-4 w-4 text-gray-400" />}
        className="h-auto py-1 px-2 hover:bg-gray-50"
        onClick={() => setIsEditing(true)}
      >
        <span className="text-gray-400">Add supplier</span>
      </Button>
    )
  }

  return (
    <Popover
      trigger="hover"
      placement="bottom"
      content={<SupplierCard supplier={selectedSupplier} />}
    >
      <Button
        type="text"
        icon={<Building className="h-4 w-4 text-gray-400" />}
        className="h-auto py-1 px-2 hover:bg-gray-50"
        onClick={() => setIsEditing(true)}
      >
        <span className="text-gray-600">
          {selectedSupplier?.name || (
            <span className="text-gray-400">Add supplier</span>
          )}
        </span>
      </Button>
    </Popover>
  )
}
